import { useState, useRef, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Mic, MicOff, Search, Loader2, X, Sparkles, MessageCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API } from '@/config/api';

// Generate unique session ID
const generateSessionId = () => `rachael-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export default function VoiceSearch({ onSearchResults }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [messages, setMessages] = useState([]);
  const [results, setResults] = useState([]);
  const [sessionId, setSessionId] = useState(generateSessionId);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Check for speech recognition support
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  // One recognizer per mount: the one the effect below configures is the one start/stop drive.
  const [recognition] = useState(() => (SpeechRecognition ? new SpeechRecognition() : null));

  // Auto scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startListening = () => {
    if (recognition) {
      setTranscript('');
      setIsListening(true);
      recognition.start();
    } else {
      toast.error('Voice recognition not supported in this browser');
    }
  };

  const stopListening = () => {
    if (recognition) {
      recognition.stop();
    }
    setIsListening(false);
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/ai/session/${sessionId}`);
    } catch (e) {
      // Ignore session clear errors
    }
    setMessages([]);
    setResults([]);
    setSessionId(generateSessionId());
    toast.success('Conversation cleared');
  };

  const processCommand = useLatestCallback(async (text) => {
    if (!text.trim()) return;
    
    // Add user message to chat
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setTranscript('');
    setIsProcessing(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, { 
        message: text,
        session_id: sessionId
      });
      
      // Add Rachael's response to chat
      const rachaelMsg = { 
        role: 'assistant', 
        content: res.data.message,
        restaurants: res.data.restaurants || []
      };
      setMessages(prev => [...prev, rachaelMsg]);
      
      if (res.data.restaurants && res.data.restaurants.length > 0) {
        setResults(res.data.restaurants);
        if (onSearchResults) {
          onSearchResults(res.data.restaurants);
        }
      }
    } catch (error) {
      console.error('AI command error:', error);
      const errorMsg = { 
        role: 'assistant', 
        content: "I couldn't process that. Please try again." 
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsProcessing(false);
    }
  });

  useEffect(() => {
    if (recognition) {
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        setTranscript(result[0].transcript);
      };

      recognition.onend = () => {
        setIsListening(false);
        if (transcript) {
          processCommand(transcript);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error('Voice recognition failed. Try typing instead.');
      };
    }
  }, [transcript, recognition, processCommand]);

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (transcript.trim()) {
      processCommand(transcript);
    }
  };

  const handleRestaurantClick = (id) => {
    setIsOpen(false);
    navigate(`/restaurant/${id}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full relative"
          data-testid="voice-search-btn"
        >
          <Sparkles className="h-5 w-5 text-primary" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Chat with Rachael
            </DialogTitle>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearChat} className="h-8 px-2">
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
          <DialogDescription>
            Your AI food assistant - Ask about restaurants, cuisines, or recommendations
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 py-2">
          {/* Chat Messages */}
          <ScrollArea className="flex-1 pr-4 mb-4">
            <div className="space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Start a conversation with Rachael!</p>
                  <p className="text-xs mt-1">Try: "Find me sushi" or "What's good for lunch?"</p>
                </div>
              )}
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                    msg.role === 'user' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  }`}>
                    {msg.role === 'assistant' && (
                      <p className="text-xs font-medium mb-1 text-primary">Rachael</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    {/* Inline restaurant results */}
                    {msg.restaurants && msg.restaurants.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {msg.restaurants.slice(0, 3).map((r) => (
                          <Card
                            key={r.id}
                            className="cursor-pointer hover:bg-background/80 transition-colors"
                            onClick={() => handleRestaurantClick(r.id)}
                          >
                            <CardContent className="p-2 flex items-center gap-2">
                              {r.image_url && (
                                <img
                                  src={r.image_url}
                                  alt={r.name}
                                  className="w-10 h-10 rounded object-cover"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{r.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  ⭐ {r.rating} • {r.delivery_time_min}-{r.delivery_time_max}m
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Voice/Text Input */}
          <form onSubmit={handleTextSubmit} className="flex gap-2 pt-2 border-t">
            <div className="flex-1 relative">
              <Input
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder={isListening ? "Listening..." : "Type or speak to Rachael..."}
                className="pr-10"
                data-testid="voice-input"
                disabled={isListening}
              />
              {transcript && !isListening && (
                <button
                  type="button"
                  onClick={() => setTranscript('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant={isListening ? "destructive" : "outline"}
              size="icon"
              onClick={isListening ? stopListening : startListening}
              disabled={isProcessing}
              data-testid="mic-btn"
            >
              {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
            <Button type="submit" disabled={!transcript.trim() || isProcessing} data-testid="search-btn">
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </form>

          {isListening && (
            <div className="text-center py-2">
              <p className="text-xs text-muted-foreground animate-pulse">🎤 Listening...</p>
            </div>
          )}

          {/* Quick Suggestions - only show when no messages */}
          {messages.length === 0 && (
            <div className="pt-3">
              <p className="text-xs text-muted-foreground mb-2">Quick suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "Find Chinese food",
                  "Best Italian restaurants",
                  "I'm hungry for Thai",
                  "What's popular today?"
                ].map((example) => (
                  <button
                    key={example}
                    onClick={() => {
                      setTranscript(example);
                      processCommand(example);
                    }}
                    className="text-xs bg-muted px-3 py-1.5 rounded-full hover:bg-muted/80 transition-colors"
                    disabled={isProcessing}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
